import type { AvailableRouterMethod, NitroFetchRequest } from 'nitropack';
import type { UseFetchOptions } from 'nuxt/app';
export type KeysOf<T> = Array<T extends T ? (keyof T extends string ? keyof T : never) : never>;

/**
 * useFetchData:
 * This function is used to handle fetching data from the server in SSR mode
 * It takes the request and the options for the fetch
 * It returns the data and the error
 *
 * @param {NitroFetchRequest} request: The request to fetch the data
 * @param {NitroFetchOptions} opts: The options for the fetch
 * @returns {data, error}
 *
 * @example
 * const {data}: {data: Ref<Product>} = await useFetchData<Product>({
 *  url: 'https://api.example.com/data',
 *  transform: (data) => data as Product
 * });
 */
export async function useFetchData<T, Error = unknown>(
  request: NitroFetchRequest,
  opts?:
    | UseFetchOptions<{}, {}, KeysOf<{}>, null, NitroFetchRequest, AvailableRouterMethod<NitroFetchRequest>>
    | undefined
) {
  // set default options
  const defaultOpts = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  return useFetch<T, Error>(request, {
    ...defaultOpts,
    ...opts,
    $fetch: useNuxtApp().$api,
  });
}
